import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { BaseHttpService } from './base-http.service';

@Injectable({
	providedIn: 'root',
})
export class ApiService extends BaseHttpService {
	login(data: any) {
		return this.post<any>(`dashboard/auth/login`, data);
	}

	refreshToken(token: string) {
		return this.post<any>(`dashboard/auth/refresh`, { refreshToken: token });
	}

	getAllProviders(limit: number, offset: number, filters: any, sortedByKey?: string, sortedByOrder?: string) {
		let url = `dashboard/providers?limit=${limit}&offset=${offset}`;
		if (sortedByKey) {
			url = url + `&sortBy=${sortedByKey}&sortOrder=${sortedByOrder}`;
		}
		if (filters.joinedFrom) {
			url = url + `&joinedFrom=${filters.joinedFrom}`;
		}
		if (filters.joinedTo) {
			url = url + `&joinedTo=${filters.joinedTo}`;
		}
		if (filters.categories) {
			url = url + `&categories=${filters.categories}`;
		}
		if (filters.skills) {
			url = url + `&skills=${filters.skills}`;
		}
		if (filters.status) {
			url = url + `&status=${filters.status}`;
		}
		if (filters.email) {
			url = url + `&email=${filters.email}`;
		}
		if (filters.phoneNumber) {
			url = url + `&phoneNumber=${filters.phoneNumber}`;
		}
		if (filters.name) {
			url = url + `&name=${filters.name}`;
		}
		if (filters.locationId) {
			url = url + `&locationId=${filters.locationId}`;
		}
		return this.get<any>(url);
	}

	searchForUsers(searchTerm: string) {
		return this.get<any>(`dashboard/users?searchText=${encodeURIComponent(searchTerm)}`);
	}

	getAllEvents(
		limit: number,
		offset: number,
		sortedByKey?: string,
		sortedByOrder?: string,
		startDate?: string,
		endDate?: string,
		categories?: string,
		status?: string,
		selectedTypes?: string,
		locationId?: string,
	) {
		let url = `dashboard/events?limit=${limit}&offset=${offset}`;
		if (sortedByKey) url = url + `&sortBy=${sortedByKey}&sortOrder=${sortedByOrder}`;
		if (startDate) url = url + `&startDate=${startDate}`;
		if (endDate) url = url + `&endDate=${endDate}`;
		if (categories) url = url + `&categories=${categories}`;
		if (status) url = url + `&status=${status}`;
		if (selectedTypes) url = url + `&type=${selectedTypes}`;
		if (locationId) url = url + `&locationId=${locationId}`;
		return this.get<any>(url);
	}

	getAllBookings(filters: any) {
		const { limit, offset, ...rest } = filters;
		let url = `dashboard/bookings?limit=${limit}&offset=${offset}`;
		for (const key in rest) {
			if (rest[key] === null || rest[key] === undefined || rest[key] === '') {
				delete rest[key];
			} else {
				url += `&${key}=${rest[key]}`;
			}
		}
		return this.get<any>(url);
	}

	getAllUsers() {
		return this.get<any>(`dashboard/customers`);
	}

	getFeeds() {
		return this.get<any>(`dashboard/feeds`);
	}

	addFeed(data: any) {
		return this.post<any>(`dashboard/feeds`, data);
	}

	deleteFeed(id: any) {
		return this.delete<any>(`dashboard/feeds/${id}`);
	}

	getFeedsById(id: any) {
		return this.get<any>(`dashboard/feeds/${id}`);
	}

	updateFeed(id: any, data: any) {
		return this.put<any>(`dashboard/feeds/${id}`, data);
	}

	uploadFiles(files: any, folderName?: string) {
		const formData: FormData = new FormData();
		for (let i in files) {
			formData.append('files', files[i]);
		}
		if (folderName) {
			formData.append('folderName', folderName);
		}

		return this.post<any>(`files/upload`, formData).pipe(map((response) => response['data']));
	}

	deleteEvent(id: string) {
		return this.delete<any>(`dashboard/events/${id}`);
	}

	exportUsersReport(payload: any) {
		return this.post<any>(`reports`, payload);
	}

	getAllInquiries(params?: any) {
		console.log(params);
		return this.get<any>(`dashboard/inquiries`, { ...params });
	}

	getInquiryQuotes(inquiryId: string) {
		return this.get<any>(`dashboard/inquiries/${inquiryId}/quotes`);
	}

	getInquiryActiveRequest(inquiryId: string) {
		return this.get<any>(`dashboard/inquiries/${inquiryId}/active-requests`);
	}

	updateProviderOfferings(providerI: string, offerings: any[]) {
		return this.put<any>(`dashboard/providers/${providerI}/offerings`, { offerings });
	}

	updateProviderCategories(providerId: string, categories: any[]) {
		return this.put<any>(`dashboard/providers/${providerId}/categories`, { categories });
	}

	resendInquiry(inquiryId: string, body: any) {
		return this.post<any>(`inquiries/${inquiryId}/resend`, body);
	}

	closeInquiry(inquiryId: string) {
		return this.post<any>(`inquiries/${inquiryId}/close`, {});
	}

	getBookingDetails(bookingId: string) {
		return this.get<any>(`dashboard/bookings/${bookingId}`);
	}

	getSearchHistory(params: any) {
		return this.get<any>(`dashboard/search-texts`, { params });
	}

	createExplanationVideo(body: any) {
		return this.post<any>(`dashboard/explanation-videos`, body);
	}

	getExplanationVideo() {
		return this.get<any>(`dashboard/explanation-videos`);
	}

	getBestDeals(limit: number, offset: number, filters: any) {
		return this.get<any>(`dashboard/best-deals`, { params: { limit, offset, ...filters } });
	}

	createBestDeal(data: any) {
		return this.post<any>(`dashboard/best-deals`, data);
	}

	updateBestDeal(id: number, data: any) {
		return this.put<any>(`dashboard/best-deals/${id}`, data);
	}

	deleteBestDeal(id: number) {
		return this.delete<any>(`dashboard/best-deals/${id}`);
	}

	updateBestDealsOrder(orderUpdate: { [dealId: string]: number }) {
		return this.patch<any>(`dashboard/best-deals/sort`, orderUpdate);
	}

	updateBestExperiencesOrder(orderUpdate: { [experienceId: string]: number }) {
		return this.patch<any>(`dashboard/best-experiences/sort`, orderUpdate);
	}

	getBestExperiences(limit: number, offset: number, filters: any) {
		return this.get<any>(`dashboard/best-experiences`, { params: { limit, offset, ...filters } });
	}

	updateBestExperience(id: number, data: any) {
		return this.put<any>(`dashboard/best-experiences/${id}`, data);
	}

	deleteBestExperience(id: number) {
		return this.delete<any>(`dashboard/best-experiences/${id}`);
	}

	createBestDealOffering(providerId: string, offering: any) {
		return this.post<any>(`dashboard/providers/${providerId}/offerings`, offering);
	}

	createBestExperience(data: any) {
		return this.post<any>(`dashboard/best-experiences`, data);
	}

	getAvailableReports() {
		return this.get<any>(`dashboard/reports`);
	}

	generateReport(data: any) {
		return this.post<any>(`dashboard/reports/generate`, data);
	}

	testSearch(searchText: string) {
		return this.get<any>(`home/test/search/${encodeURIComponent(searchText)}`);
	}

	mainSearch(searchText: string) {
		return this.get<any>(`home/search/${encodeURIComponent(searchText)}`);
	}

	sendPushNotification(data: any) {
		return this.post<any>(`push-notifications/send`, data);
	}

	sendBroadcastPushNotification(data: any) {
		return this.post<any>(`dashboard/push-notifications/send`, data);
	}

	cancelQuote(quoteId: string, cancellationReason: string) {
		return this.post<any>(`dashboard/quotes/${quoteId}/cancel`, { cancellationReason });
	}

	searchProvidersBySkill(skillId: number, name: string, limit: number = 15) {
		const url = `dashboard/providers?limit=${limit}&offset=0&status=accepted&skills=${skillId}&name=${encodeURIComponent(name)}`;
		return this.get<any>(url);
	}

	getProviderOfferings(providerId: string) {
		return this.get<any>(`dashboard/providers/${providerId}/offerings`);
	}

	createQuote(quote: any) {
		return this.post<any>(`dashboard/quotes`, quote);
	}

	getLanguages() {
		return this.get<any>(`static-data/languages`);
	}

	getLocations() {
		return this.get<any>(`dashboard/locations`);
	}

	getProvidersBySkillAndLocation(filters: any) {
		let url = `dashboard/providers/map?`;
		const params: string[] = [];

		if (filters.skillId) {
			params.push(`skillId=${filters.skillId}`);
		}
		if (filters.categoryId) {
			params.push(`categoryId=${filters.categoryId}`);
		}
		if (filters.locationId) {
			params.push(`locationId=${filters.locationId}`);
		}

		url += params.join('&');
		return this.get<any>(url);
	}
}
